import { useState, useEffect, useCallback, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';

interface EmailValidationResponse {
  isValid: boolean;
  suggestedHostName?: string;
}

interface UseEmailValidationReturn {
  isValid: boolean | null;
  suggestedHostName: string | null;
  isLoading: boolean;
  error: boolean;
}

export const useEmailValidation = (email: string, enabled: boolean = true): UseEmailValidationReturn => {
  const [validationResult, setValidationResult] = useState<EmailValidationResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const postEmailValidation = useCallback(
    async (emailToValidate: string) =>
      apiClient.post<EmailValidationResponse>(ApiType.apimMembershipApi, {
        url: ApiPaths[ApiType.apimMembershipApi].validateEmail,
        data: { email: emailToValidate },
      }),
    [],
  );

  const validateEmail = useCallback(
    async (emailToValidate: string) => {
      if (!emailToValidate) {
        setValidationResult(null);
        setIsLoading(false);
        setError(false);
        return;
      }

      if (!emailToValidate.includes('@')) {
        setValidationResult({ isValid: false });
        setIsLoading(false);
        setError(false);
        return;
      }

      setError(false);

      try {
        const response = await postEmailValidation(emailToValidate);
        setValidationResult(response.data);
      } catch (e) {
        setError(true);
        setValidationResult(null);
      } finally {
        setIsLoading(false);
      }
    },
    [postEmailValidation],
  );

  const debouncedValidateEmail = useMemo(
    () => debounce((emailToValidate: string) => validateEmail(emailToValidate), 500),
    [validateEmail],
  );

  useEffect(() => {
    if (enabled && !!email) {
      setIsLoading(true);
      debouncedValidateEmail(email);
    } else {
      setValidationResult(null);
      setIsLoading(false);
      setError(false);
    }

    return () => {
      debouncedValidateEmail.cancel();
    };
  }, [email, enabled, debouncedValidateEmail]);

  return {
    isValid: validationResult?.isValid ?? null,
    suggestedHostName: validationResult?.suggestedHostName ?? null,
    isLoading,
    error,
  };
};
